/** Free-text input step — auto-focused textarea with optional character constraints. */
import { useEffect, useRef } from 'react';
import { useSession } from '../SessionContext';
import { StepShell } from './StepShell';
import {
  PromptRenderer,
  type TiptapNode,
  type MediaItem,
} from '@/shared/components/PromptRenderer';
import { normaliseMediaRefs } from '../utils/normaliseMediaRefs';
import { FeedbackDisplay } from './FeedbackDisplay';

interface DiagramJson {
  syntax: string;
  diagramType?: string;
}

interface TextConfig {
  acceptedAnswers: string[];
  placeholder?: string;
  minLength?: number;
  maxLength?: number;
}

/**
 * Free text input step with optional character constraints.
 */
export function TextStep() {
  const { currentStep, state, dispatch } = useSession();

  const snapshot = currentStep?.stepSnapshotPublicJson;
  const config = (snapshot?.config ?? null) as unknown as TextConfig | null;
  const placeholder = config?.placeholder ?? 'Type your answer...';
  const answer = state.answer?.type === 'text' ? state.answer : null;
  const value = answer?.value ?? '';
  const phase = state.phase;
  const result = state.result;
  const isDisabled = phase === 'submitting' || phase === 'grading';
  const isGraded = phase === 'graded';
  const showCharCount = config?.minLength != null || config?.maxLength != null;

  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!isDisabled && !isGraded) {
      inputRef.current?.focus();
    }
  }, [currentStep?.id, isDisabled, isGraded]);

  const mediaItems: MediaItem[] | null = normaliseMediaRefs(currentStep?.mediaRefsJson);

  const diagramJson = (currentStep as unknown as Record<string, unknown>)?.diagramJson as
    | DiagramJson
    | null
    | undefined;

  if (!currentStep || !snapshot) return null;

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    if (isDisabled || isGraded) return;
    dispatch({ type: 'UPDATE_ANSWER', answer: { type: 'text', value: e.target.value } });
  }

  return (
    <StepShell>
      <PromptRenderer
        doc={snapshot.prompt as TiptapNode}
        media={mediaItems}
        diagramJson={diagramJson ?? null}
      />

      <div
        style={{
          opacity: isDisabled ? 0.7 : 1,
          pointerEvents: isDisabled || isGraded ? 'none' : 'auto',
        }}
      >
        <input
          ref={inputRef}
          type="text"
          value={value}
          onChange={handleChange}
          placeholder={placeholder}
          disabled={isDisabled || isGraded}
          maxLength={config?.maxLength}
          style={{
            width: '100%',
            padding: 'var(--f-space-4)',
            borderRadius: 'var(--f-radius-xl)',
            border: '2px solid var(--f-border)',
            backgroundColor: 'var(--f-bg-raised)',
            color: 'var(--f-fg)',
            fontFamily: 'var(--f-font-sans)',
            fontSize: 'var(--f-text-base)',
            lineHeight: 'var(--f-leading-relaxed)',
            outline: 'none',
            transition: 'border-color var(--f-duration-fast) ease',
          }}
          onFocus={(e) => {
            e.currentTarget.style.borderColor = 'var(--f-accent)';
          }}
          onBlur={(e) => {
            e.currentTarget.style.borderColor = 'var(--f-border)';
          }}
        />

        {showCharCount && (
          <div
            style={{
              marginTop: 'var(--f-space-2)',
              fontSize: 'var(--f-text-xs)',
              color: 'var(--f-fg-muted)',
              fontFamily: 'var(--f-font-sans)',
              textAlign: 'right',
            }}
          >
            {value.length}
            {config?.maxLength != null && ` / ${config.maxLength}`}
            {config?.minLength != null && value.length < config.minLength && (
              <span style={{ color: 'var(--f-warning-text)', marginLeft: 'var(--f-space-2)' }}>
                (min {config.minLength})
              </span>
            )}
          </div>
        )}
      </div>

      {isGraded && result && <FeedbackDisplay result={result} />}
    </StepShell>
  );
}
